import React from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogTitle, DialogContent, DialogActions, Button} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {makeStyles} from 'tss-react/mui'

interface DeleteAnnotationDialogProps {
  open: boolean
  onCancel: () => void
  onDelete: () => void
  isDeleting?: boolean
}

const useStyles = makeStyles()(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      padding: '24px',
      width: '480px',
      maxWidth: '480px'
    }
  },
  title: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: '#29333d',
    padding: 0,
    marginBottom: '24px'
  },
  content: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    color: '#29333d',
    padding: 0,
    marginBottom: '24px'
  },
  actions: {
    padding: 0,
    gap: '16px'
  },
  cancelButton: {
    flex: 1,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    padding: '12px 16px',
    borderColor: '#e8ecf0',
    color: '#29333d',
    borderRadius: '6px'
  },
  deleteButton: {
    flex: 1,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    padding: '12px 16px',
    backgroundColor: '#da0901',
    color: 'white',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: '#b90801'
    },
    '&:disabled': {
      backgroundColor: 'rgba(218, 9, 1, 0.5)'
    }
  },
  deleteIcon: {
    fontSize: '16px',
    marginRight: '8px'
  }
}))

export const DeleteAnnotationDialog: React.FC<DeleteAnnotationDialogProps> = ({
  open,
  onCancel,
  onDelete,
  isDeleting = false
}) => {
  const {t} = useTranslation()
  const {classes} = useStyles()

  return (
    <Dialog 
      open={open} 
      onClose={onCancel} 
      className={classes.dialog}
      maxWidth={false}
    >
      <DialogTitle className={classes.title}>
        {t('deleteAnnotationTitle')}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {t('deleteAnnotationMessage')}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onCancel}
          variant="outlined"
          className={classes.cancelButton}
          disabled={isDeleting}
          data-test-id="delete-annotation-cancel-button"
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={onDelete}
          variant="contained"
          className={classes.deleteButton}
          disabled={isDeleting}
          startIcon={<DeleteOutlineIcon className={classes.deleteIcon} />}
          data-test-id="delete-annotation-confirm-button"
        >
          {t('deleteAnnotation')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
